/**
 * The worker record form, shared by Workers → Add worker and the worker page's Edit. Presentational:
 * the parent owns the values, the field errors and the submit.
 */
import type { FormEvent } from 'react';
import {
  EMPLOYMENT_TYPES,
  SHIFTS,
  localYmd,
  type EmploymentType,
  type EngagementType,
  type Shift,
} from '@siva/shared';
import { FieldError, shiftLabel } from '../lib/ui.js';
import { DateField } from '../lib/DateField.js';
import { t } from '../i18n/index.js';

export type WorkerFormValues = {
  first_name: string;
  last_name: string;
  /** 10 digits; the +91 prefix is fixed in the UI and added when sending. */
  phone: string;
  employment_type: EmploymentType;
  shift: Shift;
  join_date: string;
  /** Contractor for now; employees follow and the documents differ for them. */
  engagement_type: EngagementType;
  designation_id: string | null;
};

/** For the designation dropdown. The full name is what a document prints. */
export type Designation = { id: string; code: string; name: string; active?: boolean };

export type WorkerFieldKey = 'phone';
export type WorkerFieldErrors = Partial<Record<WorkerFieldKey, string | null>>;

export const emptyWorkerForm = (): WorkerFormValues => ({
  first_name: '',
  last_name: '',
  phone: '',
  employment_type: 'full_time',
  shift: 'first',
  join_date: localYmd(new Date()), // office calendar day, not the UTC date
  engagement_type: 'employee',
  designation_id: null,
});

export const digits10 = (phone: string | null | undefined) =>
  (phone ?? '').replace(/^\+91/, '').replace(/\D/g, '').slice(0, 10);

/** JSON body for POST/PATCH /admin/workers from the form values. */
export function workerPayload(v: WorkerFormValues, _mode: 'create' | 'edit') {
  const body: Record<string, unknown> = {
    first_name: v.first_name.trim(),
    last_name: v.last_name.trim() || null,
    phone: `+91${v.phone}`,
    employment_type: v.employment_type,
    shift: v.shift,
    join_date: v.join_date,
    engagement_type: v.engagement_type,
    designation_id: v.designation_id,
  };
  return body;
}

export function WorkerForm({
  mode,
  values,
  errors,
  busy,
  onChange,
  onSubmit,
  onCancel,
  code,
  designations,
}: {
  mode: 'create' | 'edit';
  values: WorkerFormValues;
  errors: WorkerFieldErrors;
  busy: boolean;
  /** The worker's code, or the one the server will assign. Shown, never sent, never editable. */
  code?: string | null;
  /** New values; `clear` names a field whose error should be dropped because the user edited it. */
  onChange: (next: WorkerFormValues, clear?: WorkerFieldKey) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  /** Admin-managed list from Settings; empty until it loads. */
  designations?: Designation[];
}) {
  const set = <K extends keyof WorkerFormValues>(
    key: K,
    value: WorkerFormValues[K],
    clear?: WorkerFieldKey,
  ) => onChange({ ...values, [key]: value }, clear);

  return (
    <form
      onSubmit={onSubmit}
      className="grid-form"
      aria-label={mode === 'edit' ? t('worker_edit') : t('workers_add')}
    >
      <label className="readonly-field">
        {t('worker_code')}
        <input value={code ?? '—'} readOnly disabled aria-describedby="worker-code-note" />
        <small id="worker-code-note" className="field-hint muted">
          {mode === 'create' ? t('worker_code_preview') : t('worker_code_fixed')}
        </small>
      </label>
      <label>
        {t('worker_first_name')}
        <input
          value={values.first_name}
          onChange={(e) => set('first_name', e.target.value)}
          required
          maxLength={60}
        />
      </label>
      <label>
        {t('worker_last_name')}
        <input
          value={values.last_name}
          onChange={(e) => set('last_name', e.target.value)}
          maxLength={60}
        />
      </label>
      <label htmlFor="worker-phone">
        {t('worker_phone')}
        <span className="phone-field">
          <span className="prefix" aria-hidden="true">
            +91
          </span>
          <input
            id="worker-phone"
            aria-label={t('worker_phone')}
            type="tel"
            inputMode="numeric"
            pattern="[6-9][0-9]{9}"
            maxLength={10}
            value={values.phone}
            onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10), 'phone')}
            required
            placeholder="98xxxxxxxx"
            title={t('worker_phone_hint')}
            aria-invalid={errors.phone ? true : undefined}
            aria-describedby={errors.phone ? 'worker-phone-error' : undefined}
          />
        </span>
        <FieldError id="worker-phone-error" message={errors.phone} />
      </label>
      {/* No "Employed As" here since 16 September 2026. The choice was Contractor or
          "Employee (coming soon)", drove nothing but a column, and every document says employee.
          The column stays; new workers are written as employees. */}
      <label>
        {t('worker_designation')}
        <select
          value={values.designation_id ?? ''}
          onChange={(e) => set('designation_id', e.target.value || null)}
        >
          <option value="">{t('worker_designation_none')}</option>
          {(designations ?? [])
            .filter((d) => d.active !== false || d.id === values.designation_id)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.code})
              </option>
            ))}
        </select>
      </label>
      <label>
        {t('worker_type')}
        <select
          value={values.employment_type}
          onChange={(e) => set('employment_type', e.target.value as EmploymentType)}
        >
          {EMPLOYMENT_TYPES.map((et) => (
            <option key={et} value={et}>
              {t(et)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t('worker_shift')}
        <select value={values.shift} onChange={(e) => set('shift', e.target.value as Shift)}>
          {SHIFTS.map((s) => (
            <option key={s} value={s}>
              {shiftLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t('worker_join_date')}
        <DateField value={values.join_date} onChange={(v) => set('join_date', v)} required />
      </label>
      <div className="row">
        <button type="submit" disabled={busy}>
          {t('save')}
        </button>
        <button type="button" className="secondary" onClick={onCancel}>
          {t('cancel')}
        </button>
      </div>
    </form>
  );
}
